import {Select, SmallLoader} from '@kit';

import React, {useCallback, useEffect, useMemo} from 'react';

import {Class, Feature, useClassQuery, useSubClassesClassQuery, useSubClassQuery} from '../../../generated/types';
import {ClassTable} from '../../../components/class-table';
import {EditHoc} from '../../../components/edit-hoc';
import {FeatureSeo, FeatureView, groupFeatures, PartialFeature} from '../../../components/feature-view';
import {buildUrl, replaceRoute, useRoute} from '../../../lib/routes';
import {classGenitive} from '../seo';
import {getApolloError, uppercaseFirstLetter} from '../../../components/utils';
import {withSubClassFeatures} from './sub-class-features';

import css from './features.module.css';
import pageCss from '../../../components/common.module.css';

type Props = {
	classId: string;
};

export const ClassFeatures: React.FC<Props> = ({classId}) => {
	const {data, error, loading} = useClassQuery({variables: {id: classId}, ssr: true});
	const heroClass = data?.class;

	useEffect(() => {
		function stop(e: TouchEvent) {
			e.stopPropagation();
		}
		const elements = document.querySelectorAll('.mdGrid') as any as HTMLElement[];
		elements.forEach((element) => {
			element.addEventListener('touchstart', stop);
			element.addEventListener('touchcancel', stop);
			element.addEventListener('touchmove', stop);
			element.addEventListener('touchend', stop);
		});

		return () => {
			elements.forEach((element) => {
				element.removeEventListener('touchstart', stop);
				element.removeEventListener('touchcancel', stop);
				element.removeEventListener('touchmove', stop);
				element.removeEventListener('touchend', stop);
			});
		};
	}, []);

	const {routeName, query} = useRoute();

	const selectedSubClassId = typeof query.subClass === 'string' ? query.subClass : '';
	const {data: subClassesData} = useSubClassesClassQuery({variables: {id: classId}});
	const subClasses = useMemo(() => subClassesData?.class?.subClasses || [], [subClassesData]);
	const {data: subClassData} = useSubClassQuery({
		variables: {id: selectedSubClassId},
		skip: !selectedSubClassId,
	});
	const subClass = subClassData?.subClass;

	const handleSelectSubClass = useCallback(
		(values: Array<string | number | null>) => {
			// выбор живёт в адресе, но canonical его не знает: он строится без query
			replaceRoute('class', {id: classId, subClass: values[0] ? String(values[0]) : null}, {scroll: false});
		},
		[classId],
	);

	const classFeatures = useMemo(() => {
		if (heroClass) return groupFeatures(heroClass.features as Feature[]);
		return [];
	}, [heroClass]);

	const subClassFeatures = useMemo(() => {
		if (!subClass?.features) return [];
		return groupFeatures(subClass.features as Feature[]).filter((feature) => !feature.parentId);
	}, [subClass]);

	const subClassFeatureIds = useMemo(
		() => new Set(subClassFeatures.map((feature) => feature.id)),
		[subClassFeatures],
	);

	const features = useMemo(
		() => withSubClassFeatures(classFeatures, subClassFeatures),
		[classFeatures, subClassFeatures],
	);
	const baseFeatures = useMemo(() => features.filter((f: PartialFeature) => !f.parentId), [features]);
	const openFeature =
		routeName === 'classFeature'
			? heroClass?.features?.find((feature) => feature.id === query.featureId)
			: undefined;

	return (
		<>
			{heroClass && openFeature && (
				<FeatureSeo
					feature={openFeature}
					section="classes"
					owner={`умение ${classGenitive(heroClass)}`}
					lead={`Умение класса ${heroClass.title}`}
					canonicalPath={buildUrl('class', {id: heroClass.id})}
					crumbs={[
						{name: heroClass.title, path: buildUrl('class', {id: heroClass.id})},
						{
							name: openFeature.title,
							path: buildUrl('classFeature', {id: heroClass.id, featureId: openFeature.id}),
						},
					]}
				/>
			)}
			{loading && (
				<div className={pageCss.fullSizeBlock}>
					<SmallLoader />
				</div>
			)}
			{error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>}
			{heroClass && (
				<div>
					<div className={css.heading}>
						<h1>Особенности {uppercaseFirstLetter(heroClass.cases.genitive)}</h1>
						{subClasses.length > 0 && (
							<Select
								className={css.subClass}
								items={subClasses.map((item) => ({value: item.id, label: item.title}))}
								value={selectedSubClassId || null}
								onChange={handleSelectSubClass}
								placeholder="Подкласс не выбран"
							/>
						)}
					</div>
					<ClassTable heroClass={heroClass as Class} features={features} />

					<EditHoc
						load={() => import('../edit-buttons').then((m) => m.EditClass)}
						editProps={{id: heroClass.id}}
					>
						<h1 className={css.mainHeading}>
							Классовые особенности {uppercaseFirstLetter(heroClass.cases.genitive)}
						</h1>
						Вы получаете описанные далее классовые особенности при достижении определённых уровней{' '}
						{uppercaseFirstLetter(heroClass.cases.genitive)}. Все особенности перечислены в таблице
						«Особенности {uppercaseFirstLetter(heroClass.cases.genitive)}».
					</EditHoc>
					<div className={pageCss.columns}>
						{baseFeatures.map((feature) =>
							subClassFeatureIds.has(feature.id) && subClass ? (
								<FeatureView
									feature={feature}
									key={feature.id}
									parentObject={{type: 'subClass', parentId: subClass.id, supParentId: heroClass.id}}
									note={`Особенность подкласса «${subClass.title}»`}
								/>
							) : (
								<FeatureView
									feature={feature}
									key={feature.id}
									parentObject={{type: 'class', parentId: heroClass.id}}
								/>
							),
						)}
					</div>
				</div>
			)}
		</>
	);
};
